/**
 * WorkflowPanels — small white "interface" panels that sit at the bottom of the
 * Workflow step cards and illustrate a process with a subtle looping animation
 * (CSS keyframes in index.css; reduced-motion safe). Same spirit as the dashboard
 * mini-UI, so they use the interface type sizes (sub-14 is the sanctioned UI
 * exception). Content-specific illustrations for the 2027-law section.
 */
import { Check, ShieldCheck, FileSpreadsheet, X } from "lucide-react";

const PANEL = "rounded-2xl border border-line bg-white p-3.5 font-ds";

/** 01 — hours registered digitally, tamper-proof. */
export function RegisterPanel() {
  return (
    <div className={PANEL}>
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ds-accent/10 text-ds-accent">
          <ShieldCheck size={17} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-ds-ink">Albert · Check-in</div>
          <div className="text-[11px] text-ds-ink-soft">08:32 · on site · tamper-proof</div>
        </div>
        <span className="ds-pop grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ds-pos text-white">
          <Check size={15} strokeWidth={3} />
        </span>
      </div>
    </div>
  );
}

/** 02 — mandatory from 1 January 2027: a week strip with the deadline date
 *  hand-circled (the ring draws itself on a loop). */
const WEEK_DAYS = ["M", "T", "W", "T", "F", "S", "S"];
const WEEK_DATES: { n: string; dim?: boolean; mark?: boolean }[] = [
  { n: "28", dim: true }, { n: "29", dim: true }, { n: "30", dim: true }, { n: "31", dim: true },
  { n: "1", mark: true }, { n: "2" }, { n: "3" },
];

export function DeadlinePanel() {
  return (
    <div className="rounded-2xl border border-line bg-white p-2 font-ds">
      <div className="pb-2 pt-1 text-center text-[14px] font-bold tracking-tight text-ds-ink">January 2027</div>
      <div className="grid grid-cols-7 gap-1">
        {WEEK_DAYS.map((d, i) => (
          <span key={`d${i}`} className="pb-0.5 text-center text-[9px] font-semibold uppercase text-ds-ink-soft/50">{d}</span>
        ))}
        {WEEK_DATES.map((c, i) => (
          <span key={`n${i}`} className={`relative grid h-12 place-items-center rounded-lg bg-bg-subtle text-[13px] font-semibold ${c.dim ? "text-ds-ink-soft/40" : "text-ds-ink"}`}>
            <span className="relative z-10">{c.n}</span>
            {c.mark && (
              <svg viewBox="0 0 40 36" fill="none" aria-hidden className="absolute left-1/2 top-1/2 h-9 w-10 -translate-x-1/2 -translate-y-1/2">
                <ellipse className="ds-circle-draw" pathLength={100} cx="20" cy="18" rx="16" ry="13" stroke="var(--ds-accent)" strokeWidth="2.5" strokeLinecap="round" transform="rotate(-8 20 18)" />
              </svg>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

/** 03 — paper & Excel won't qualify. */
export function RiskPanel({ badge = "Won't qualify" }: { badge?: string }) {
  return (
    <div className={PANEL}>
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-bg-subtle text-ds-ink-soft">
          <FileSpreadsheet size={17} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-ds-ink">timesheet.xlsx</div>
          <div className="text-[11px] text-ds-ink-soft">Paper · Excel</div>
        </div>
        <span className="ds-pulse-ring inline-flex shrink-0 items-center gap-1 rounded-full bg-red-500/10 px-2 py-1 text-[10px] font-semibold text-red-500">
          <X size={11} strokeWidth={3} /> {badge}
        </span>
      </div>
    </div>
  );
}
